import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, UserCog, CalendarDays, Clock, 
  Receipt, FileText, Bell, MessageSquare, GraduationCap, 
  HeartPulse, ShieldAlert, Banknote, Shield, LogOut, X, 
  Building2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ROLE_META } from '../../utils/roles';

export default function ESSSidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;

  const meta = ROLE_META[user.role] || { label: user.role, color: '#6C63FF' };
  const initials = user.full_name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?';

  // Explicit employee menu items as requested by client
  const menuItems = [
    { label: 'Dashboard', href: '/ess', icon: LayoutDashboard },
    { label: 'My Profile', href: '/ess/profile', icon: UserCog },
    { label: 'Apply Leave', href: '/ess/leave?action=new', icon: CalendarDays },
    { label: 'Leave History', href: '/ess/leave', icon: CalendarDays },
    { label: 'Attendance', href: '/ess/attendance', icon: Clock },
    { label: 'My Payslips', href: '/ess/payslips', icon: Receipt },
    { label: 'Loans & Advances', href: '/ess/loans', icon: Banknote },
    { label: 'Documents', href: '/ess/documents', icon: FileText },
    { label: 'Notifications', href: '/ess/notifications', icon: Bell },
    { label: 'Help Desk', href: '/ess/help-desk', icon: MessageSquare },
    ...(user.role !== 'employee' ? [{ label: 'Settings', href: '/ess/settings', icon: Shield }] : [])
  ];

  const handleLogout = () => {
    logout();
  };

  const isLinkActive = (item) => {
    const currentPath = location.pathname + location.search;
    if (item.href === '/ess') {
      return currentPath === '/ess';
    }
    if (item.href.includes('?')) {
      return currentPath === item.href;
    }
    if (location.search && !item.href.includes('?')) {
      return false;
    }
    return location.pathname === item.href;
  };

  return (
    <>
      <div
        className={`sidebar-backdrop ${open ? 'show' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`app-sidebar ${open ? 'open' : ''}`}>
        <div style={{
          padding: '12px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--sidebar-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img 
              src="/logo.png" 
              alt="Linqsoft Logo" 
              style={{ height: '32px', width: 'auto', objectFit: 'contain' }} 
            />
          </div>
          <button className="sidebar-close-btn btn btn-icon" onClick={onClose} style={{ color: 'var(--sidebar-muted)', background: 'none', border: 'none' }} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--sidebar-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `${meta.color}28`, border: `1px solid ${meta.color}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: meta.color,
            }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.88)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.full_name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--sidebar-muted)', marginTop: 1 }}>
                {meta.label}
              </div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 10px' }}>
          {menuItems.map(item => {
            const active = isLinkActive(item);
            return (
              <NavLink
                key={item.label}
                to={item.href}
                end={item.href === '/ess'}
                onClick={onClose}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 8, marginBottom: 2,
                  fontSize: 13.5, fontWeight: active ? 500 : 400,
                  color: active ? 'var(--sidebar-active)' : 'var(--sidebar-text)',
                  background: active ? 'var(--sidebar-active-bg)' : 'transparent',
                  textDecoration: 'none', transition: 'all 0.12s ease',
                  position: 'relative',
                }}
                onMouseOver={e => {
                  if (!active) {
                    e.currentTarget.style.background = 'var(--sidebar-hover)';
                  }
                }}
                onMouseOut={e => {
                  if (!active) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <>
                  {active && (
                    <div style={{
                      position: 'absolute', left: 0, top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3, height: 16, borderRadius: '0 3px 3px 0',
                      background: 'var(--sidebar-active)',
                    }} />
                  )}
                  <item.icon size={15} style={{ opacity: active ? 1 : 0.65, flexShrink: 0 }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.label}
                  </span>
                  {active && <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--sidebar-active)', opacity: 0.8 }} />}
                </>
              </NavLink>
            );
          })}
        </nav>

        <div style={{ padding: '10px', borderTop: '1px solid var(--sidebar-border)' }}>
          {user.role !== 'employee' && (
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '9px 12px', borderRadius: 8,
                fontSize: 13.5, color: '#3B82F6', textDecoration: 'none',
                background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.12s', marginBottom: 4,
                textAlign: 'left'
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(59,130,246,0.08)'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            >
              <Building2 size={15} style={{ opacity: 0.8 }} />
              HR Admin Portal
            </button>
          )}
          <button
            onClick={handleLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '9px 12px', borderRadius: 8,
              fontSize: 13.5, color: 'rgba(252,110,110,0.75)',
              background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.12s',
              textAlign: 'left'
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          >
            <LogOut size={15} style={{ opacity: 0.8 }} />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
